import React, { useState, useCallback, lazy, Suspense } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const HttpProtocolCheck = lazy(() => import('../tools/HttpProtocolCheck'));
const CompetitorBenchmark = lazy(() => import('../tools/CompetitorBenchmark'));
const AnimationFpsPanel = lazy(() => import('../tools/AnimationFpsPanel'));
const ABTestPanel = lazy(() => import('../tools/ABTestPanel'));
const LazyLoadingAudit = lazy(() => import('./LazyLoadingAudit'));
const ResourceHintsAudit = lazy(() => import('./ResourceHintsAudit'));
const CDNPerformanceCheck = lazy(() => import('./CDNPerformanceCheck'));
const ConversionImpactCalculator = lazy(() => import('./ConversionImpactCalculator'));
const NetworkWaterfall = lazy(() => import('./NetworkWaterfall'));
const InfrastructureSavingsCalculator = lazy(() => import('./InfrastructureSavingsCalculator'));

const LabTestPanel = ({ origin, user, onShowPricing }) => {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [hasAccess, setHasAccess] = useState(null);
    const [device, setDevice] = useState('mobile');
    const [throttling, setThrottling] = useState('fast3g');
    const [cmpMode, setCmpMode] = useState('default');
    const [csrfToken, setCsrfToken] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedModules, setSelectedModules] = useState(['basic', 'lighthouse', 'accessibility', 'thirdParty', 'coverage']);

    const getCsrfToken = useCallback(async () => {
        if (csrfToken) return csrfToken;
        try {
            const res = await fetch('/api/csrf-token');
            const data = await res.json();
            setCsrfToken(data.token);
            return data.token;
        } catch (error) {
            console.error('Failed to get CSRF token:', error);
            return null;
        }
    }, [csrfToken]);

    const checkAccess = async () => {
        try {
            const res = await fetch('/api/lab-test/availability');
            if (res.ok) {
                const data = await res.json();
                setHasAccess(data.has_access);
            } else {
                setHasAccess(false);
            }
        } catch (error) {
            setHasAccess(false);
        }
    };

    React.useEffect(() => {
        if (user) {
            checkAccess();
        } else {
            setHasAccess(false);
        }
    }, [user]);

    const runTest = async () => {
        if (!origin) return;
        
        setLoading(true);
        setError(null);
        setResult(null);
        
        try {
            const token = await getCsrfToken();
            const res = await fetch('/api/lab-test', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': token
                },
                credentials: 'include',
                body: JSON.stringify({ 
                    url: origin.startsWith('http') ? origin : `https://${origin}`,
                    device,
                    throttling,
                    cmpMode,
                    modules: selectedModules
                })
            });
            
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error(t('lab.serverError'));
            }
            
            const data = await res.json();
            
            if (res.ok && data.success) {
                setResult(data.result);
                setActiveTab('overview');
            } else {
                setError(data.error || t('lab.testFailed'));
            }
        } catch (err) {
            setError(err.message || t('lab.failedToStart'));
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        switch (score) {
            case 'good': return 'text-green-400 bg-green-500/20';
            case 'needs-improvement': return 'text-yellow-400 bg-yellow-500/20';
            case 'poor': return 'text-red-400 bg-red-500/20';
            default: return 'text-gray-400 bg-gray-500/20';
        }
    };

    const getLighthouseScoreColor = (score) => {
        if (score >= 90) return 'text-green-400 border-green-500';
        if (score >= 50) return 'text-yellow-400 border-yellow-500';
        return 'text-red-400 border-red-500';
    };

    const getImpactColor = (impact) => {
        switch (impact) {
            case 'critical': return 'bg-red-500/30 text-red-300';
            case 'serious': return 'bg-orange-500/30 text-orange-300';
            case 'moderate': return 'bg-yellow-500/30 text-yellow-300';
            case 'minor': return 'bg-blue-500/30 text-blue-300';
            default: return 'bg-gray-500/30 text-gray-300';
        }
    };

    const getHealthScoreColor = (score) => {
        if (score >= 90) return { text: 'text-green-400', bg: 'bg-green-500', border: 'border-green-500' };
        if (score >= 70) return { text: 'text-yellow-400', bg: 'bg-yellow-500', border: 'border-yellow-500' };
        if (score >= 50) return { text: 'text-orange-400', bg: 'bg-orange-500', border: 'border-orange-500' };
        return { text: 'text-red-400', bg: 'bg-red-500', border: 'border-red-500' };
    };

    const getCategoryIcon = (category) => {
        const icons = {
            'ad': '📢',
            'analytics': '📊',
            'cdn': '🌐',
            'content': '📰',
            'customer-success': '💬',
            'hosting': '🏠',
            'marketing': '📧',
            'social': '👥',
            'tag-manager': '🏷️',
            'utility': '🔧',
            'video': '🎬',
            'consent-provider': '🍪',
            'unknown': '❓'
        };
        return icons[category] || '❓';
    };

    const getCategoryColor = (category) => {
        const colors = {
            'ad': 'bg-red-500/20 text-red-300 border-red-500/30',
            'analytics': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
            'cdn': 'bg-green-500/20 text-green-300 border-green-500/30',
            'content': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
            'customer-success': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
            'marketing': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
            'social': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
            'tag-manager': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
            'utility': 'bg-gray-500/20 text-gray-300 border-gray-500/30',
            'video': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
            'consent-provider': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
            'unknown': 'bg-slate-500/20 text-slate-300 border-slate-500/30'
        };
        return colors[category] || colors['unknown'];
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatMs = (ms) => {
        if (ms === null || ms === undefined) return '-';
        if (ms < 1000) return `${Math.round(ms)} ms`;
        return `${(ms / 1000).toFixed(2)} s`;
    };

    const tabs = [
        { id: 'overview', label: t('lab.tabs.overview'), icon: '📊', always: true },
        { id: 'lighthouse', label: 'Lighthouse', icon: '🔦', module: 'lighthouse', hasData: result?.lighthouse && !result.lighthouse.error },
        { id: 'accessibility', label: t('lab.tabs.accessibility'), icon: '♿', module: 'accessibility', hasData: result?.accessibility && !result.accessibility.error },
        { id: 'thirdParty', label: 'Third-Party', icon: '🔗', module: 'thirdParty', hasData: result?.thirdParty && !result.thirdParty.error },
        { id: 'coverage', label: 'Coverage', icon: '📈', module: 'coverage', hasData: result?.coverage && !result.coverage.error },
        { id: 'security', label: language === 'cs' ? 'Bezpečnost' : 'Security', icon: '🔒', module: 'security', hasData: result?.security && !result.security.error },
        { id: 'schema', label: 'SEO Schema', icon: '📋', module: 'schema', hasData: result?.schema && !result.schema.error },
        { id: 'techstack', label: 'Tech Stack', icon: '⚙️', module: 'techstack', hasData: result?.techstack && !result.techstack.error },
        { id: 'dnsssl', label: 'DNS & SSL', icon: '🔐', module: 'dnsssl', hasData: result?.dnsssl && !result.dnsssl.error },
        { id: 'carbon', label: language === 'cs' ? 'Uhlíková stopa' : 'Carbon', icon: '🌱', module: 'carbon', hasData: result?.carbon && !result.carbon.error },
        { id: 'gtm', label: 'GTM Audit', icon: '📊', module: 'gtm', hasData: result?.gtm && !result.gtm.error },
        { id: 'media', label: language === 'cs' ? 'Média' : 'Media', icon: '🖼️', module: 'media', hasData: result?.media && !result.media.error },
        { id: 'accessibilityAudit', label: language === 'cs' ? 'WCAG Audit' : 'WCAG Audit', icon: '♿', module: 'accessibilityAudit', hasData: result?.accessibilityAudit && !result.accessibilityAudit.error },
        { id: 'statuspage', label: 'Status Page', icon: '📡', module: 'statuspage', hasData: result?.statuspage && !result.statuspage.error },
        { id: 'uptime', label: 'Uptime', icon: '⏱️', module: 'uptime', hasData: result?.uptime && !result.uptime.error },
        { id: 'redirects', label: language === 'cs' ? 'Přesměrování' : 'Redirects', icon: '↪️', module: 'redirects', hasData: result?.redirects && !result.redirects.error },
        { id: 'brokenlinks', label: language === 'cs' ? 'Nefunkční odkazy' : 'Broken Links', icon: '🔗', module: 'brokenlinks', hasData: result?.brokenlinks && !result.brokenlinks.error },
        { id: 'httpprotocol', label: 'HTTP/2 & HTTP/3', icon: '🌐', module: 'httpprotocol', premium: true },
        { id: 'competitor', label: language === 'cs' ? 'Benchmark' : 'Benchmark', icon: '📊', module: 'competitor', premium: true },
        { id: 'animationfps', label: language === 'cs' ? 'Animace' : 'Animation', icon: '🎬', module: 'animationfps', premium: true },
        { id: 'abtest', label: 'A/B Test', icon: '🔬', module: 'abtest', premium: true },
        { id: 'lazyloading', label: language === 'cs' ? 'Lazy Loading' : 'Lazy Loading', icon: '🖼️', module: 'lazyloading', interactive: true },
        { id: 'resourcehints', label: language === 'cs' ? 'Preload/Prefetch' : 'Preload/Prefetch', icon: '⚡', module: 'resourcehints', interactive: true },
        { id: 'cdncheck', label: 'CDN Check', icon: '🌍', module: 'cdncheck', interactive: true },
        { id: 'conversioncalc', label: language === 'cs' ? 'Konverze' : 'Conversions', icon: '💰', module: 'conversioncalc', interactive: true },
        { id: 'waterfall', label: 'Waterfall', icon: '🌊', module: 'waterfall', interactive: true },
        { id: 'infrasavings', label: language === 'cs' ? 'Úspora hostingu' : 'Hosting Savings', icon: '🖥️', module: 'infrasavings', interactive: true }
    ];
    
    const filteredTabs = tabs.filter(tab => {
        if (tab.always) return true;
        if (!tab.module) return false;
        const isSelected = selectedModules.includes(tab.module);
        if (tab.premium || tab.interactive) return isSelected;
        return isSelected && tab.hasData;
    });

    if (hasAccess === null) {
        return null;
    }

    if (!hasAccess) {
        return (
            <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-xl p-6 border border-blue-500/30">
                <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🔬</span>
                    <h3 className="text-lg font-semibold text-white">Lab Test</h3>
                    <span className="px-2 py-0.5 text-xs bg-purple-500/30 text-purple-300 rounded">Premium</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                    {t('lab.description')}
                </p>
                <button 
                    onClick={onShowPricing}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                >
                    {t('lab.viewPremium')} →
                </button>
            </div>
        );
    }

    return (
        <div className="bg-gray-900/50 rounded-xl border border-gray-800">
            <div className="p-4 border-b border-gray-800">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">🔬</span>
                        <div>
                            <h3 className="text-lg font-semibold text-white">{t('lab.title')}</h3>
                            <p className="text-xs text-gray-500">{t('lab.subtitle')}</p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-wrap">
                        <select
                            value={device}
                            onChange={(e) => setDevice(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
                            disabled={loading}
                        >
                            <option value="mobile">{t('lab.devices.mobile')}</option>
                            <option value="desktop">{t('lab.devices.desktop')}</option>
                        </select>
                        
                        <select
                            value={throttling}
                            onChange={(e) => setThrottling(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
                            disabled={loading}
                        >
                            <option value="none">{t('lab.throttling.none')}</option>
                            <option value="fast3g">Fast 3G</option>
                            <option value="slow3g">Slow 3G</option>
                        </select>
                        
                        <select
                            value={cmpMode}
                            onChange={(e) => setCmpMode(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
                            disabled={loading}
                            title={t('lab.cmpMode.title')}
                        >
                            <option value="default">{t('lab.cmpMode.withBar')}</option>
                            <option value="auto-consent">{t('lab.cmpMode.autoConsent')}</option>
                        </select>
                        
                        <button
                            onClick={runTest}
                            disabled={loading || !origin}
                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 text-white rounded text-sm transition-colors"
                        >
                            {loading ? t('lab.testing') : t('lab.runAudit')}
                        </button>
                    </div>
                </div>
            </div>
            
            {loading && (
                <div className="p-8 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-400">{t('lab.runningAudit')}</p>
                    <p className="text-gray-500 text-sm mt-1">{t('lab.auditDuration')}</p>
                </div>
            )}
            
            {error && (
                <div className="p-4 m-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
                    {error}
                </div>
            )}
            
            {result && !loading && (
                <>
                    <div className="flex border-b border-gray-800 overflow-x-auto">
                        {filteredTabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                                    activeTab === tab.id 
                                        ? tab.premium 
                                            ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10'
                                            : 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10' 
                                        : tab.premium 
                                            ? 'text-purple-300/70 hover:text-purple-300'
                                            : 'text-gray-400 hover:text-gray-300'
                                }`}
                            >
                                <span>{tab.icon}</span>
                                {tab.label}
                                {tab.premium && <span className="text-[10px] px-1 py-0.5 bg-purple-500/30 text-purple-300 rounded">PRO</span>}
                            </button>
                        ))}
                    </div>
                    
                    <div className="p-4">
                        {(() => {
                            const currentTab = tabs.find(tb => tb.id === activeTab);
                            if (['lazyloading', 'resourcehints', 'cdncheck'].includes(activeTab)) {
                                return (
                                    <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                        <span className="text-xs font-medium text-amber-300 whitespace-nowrap">{t('lab.cmpBadge.noCmp')}</span>
                                        <span className="text-xs text-amber-200/70">{t('lab.cmpBadge.psiNote')}</span>
                                    </div>
                                );
                            }
                            if (!currentTab || currentTab.premium || currentTab.interactive || !result?.cmpMode) return null;
                            const granted = result.cmpMode === 'auto-consent' && result.cmpClicked;
                            const attempted = result.cmpMode === 'auto-consent' && !result.cmpClicked;
                            const label = granted
                                ? t('lab.cmpBadge.consentGranted')
                                : attempted
                                    ? t('lab.cmpBadge.consentAttemptFailed')
                                    : t('lab.cmpBadge.noCmp');
                            const color = granted
                                ? 'bg-green-500/10 border-green-500/20 text-green-300'
                                : 'bg-amber-500/10 border-amber-500/20 text-amber-300';
                            return (
                                <div className={`mb-4 flex items-start gap-2 px-3 py-2 rounded-lg border ${color}`}>
                                    <span className="text-xs font-medium whitespace-nowrap">{label}</span>
                                    <span className="text-xs opacity-70">{t('lab.cmpBadge.runScope')}</span>
                                </div>
                            );
                        })()}
                        {activeTab === 'overview' && (
                            <OverviewTab result={result} formatMs={formatMs} formatBytes={formatBytes} getScoreColor={getScoreColor} getLighthouseScoreColor={getLighthouseScoreColor} getHealthScoreColor={getHealthScoreColor} t={t} />
                        )}
                        
                        {activeTab === 'lighthouse' && result.lighthouse && !result.lighthouse.error && (
                            <LighthouseTab lighthouse={result.lighthouse} formatMs={formatMs} getLighthouseScoreColor={getLighthouseScoreColor} t={t} />
                        )}
                        
                        {activeTab === 'accessibility' && result.accessibility && !result.accessibility.error && (
                            <AccessibilityTab accessibility={result.accessibility} getImpactColor={getImpactColor} t={t} />
                        )}
                        
                        {activeTab === 'thirdParty' && result.thirdParty && !result.thirdParty.error && (
                            <ThirdPartyTab thirdParty={result.thirdParty} formatBytes={formatBytes} getCategoryIcon={getCategoryIcon} getCategoryColor={getCategoryColor} t={t} />
                        )}
                        
                        {activeTab === 'coverage' && result.coverage && !result.coverage.error && (
                            <CoverageTab coverage={result.coverage} formatBytes={formatBytes} t={t} />
                        )}
                        
                        {activeTab === 'httpprotocol' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <HttpProtocolCheck url={origin} currentUser={user} />
                            </Suspense>
                        )}
                        
                        {activeTab === 'competitor' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <CompetitorBenchmark url={origin} currentUser={user} />
                            </Suspense>
                        )}
                        
                        {activeTab === 'animationfps' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <AnimationFpsPanel url={origin} currentUser={user} />
                            </Suspense>
                        )}
                        
                        {activeTab === 'abtest' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <ABTestPanel url={origin} currentUser={user} />
                            </Suspense>
                        )}

                        {activeTab === 'lazyloading' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <LazyLoadingAudit url={origin} />
                            </Suspense>
                        )}

                        {activeTab === 'resourcehints' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <ResourceHintsAudit url={origin} />
                            </Suspense>
                        )}

                        {activeTab === 'cdncheck' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <CDNPerformanceCheck url={origin} />
                            </Suspense>
                        )}

                        {activeTab === 'conversioncalc' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <ConversionImpactCalculator />
                            </Suspense>
                        )}

                        {activeTab === 'waterfall' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <NetworkWaterfall url={origin} />
                            </Suspense>
                        )}

                        {activeTab === 'infrasavings' && (
                            <Suspense fallback={<div className="text-center py-8 text-gray-400">{t('common.loading')}</div>}>
                                <InfrastructureSavingsCalculator pageSize={result?.lighthouse?.totalByteWeight || result?.resources?.totalSize} />
                            </Suspense>
                        )}

                        {activeTab === 'security' && result.security && !result.security.error && (
                            <SecurityTab data={result.security} t={t} />
                        )}

                        {activeTab === 'schema' && result.schema && !result.schema.error && (
                            <SchemaTab data={result.schema} t={t} />
                        )}

                        {activeTab === 'techstack' && result.techstack && !result.techstack.error && (
                            <TechStackTab data={result.techstack} t={t} />
                        )}

                        {activeTab === 'dnsssl' && result.dnsssl && !result.dnsssl.error && (
                            <DnsSslTab data={result.dnsssl} t={t} />
                        )}

                        {activeTab === 'carbon' && result.carbon && !result.carbon.error && (
                            <CarbonTab data={result.carbon} t={t} />
                        )}

                        {activeTab === 'gtm' && result.gtm && !result.gtm.error && (
                            <GtmTab data={result.gtm} t={t} />
                        )}

                        {activeTab === 'media' && result.media && !result.media.error && (
                            <MediaTab data={result.media} t={t} />
                        )}

                        {activeTab === 'accessibilityAudit' && result.accessibilityAudit && !result.accessibilityAudit.error && (
                            <AccessibilityAuditTab data={result.accessibilityAudit} t={t} />
                        )}

                        {activeTab === 'statuspage' && result.statuspage && !result.statuspage.error && (
                            <StatusPageTab data={result.statuspage} t={t} />
                        )}

                        {activeTab === 'uptime' && result.uptime && !result.uptime.error && (
                            <UptimeTab data={result.uptime} t={t} />
                        )}

                        {activeTab === 'redirects' && result.redirects && !result.redirects.error && (
                            <RedirectsTab data={result.redirects} t={t} />
                        )}

                        {activeTab === 'brokenlinks' && result.brokenlinks && !result.brokenlinks.error && (
                            <BrokenLinksTab data={result.brokenlinks} t={t} />
                        )}
                    </div>
                    
                    <div className="px-4 pb-4 flex items-center justify-between text-xs text-gray-500">
                        <span>{t('lab.test')}: {new Date(result.timestamp).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US')}</span>
                        <span>{t('lab.totalTime')}: {formatMs(result.loadTime)}</span>
                    </div>
                </>
            )}
        </div>
    );
};

const OverviewTab = ({ result, formatMs, formatBytes, getScoreColor, getLighthouseScoreColor, getHealthScoreColor, t }) => {
    const healthColors = result.healthScore ? getHealthScoreColor(result.healthScore.score) : null;
    
    return (
    <div className="space-y-6">
        <div className="flex gap-4">
            {result.healthScore && (
                <div className="w-32 flex-shrink-0 text-center">
                    <div className={`w-24 h-24 mx-auto rounded-full border-4 flex flex-col items-center justify-center ${healthColors.border} bg-gray-800/50`}>
                        <div className={`text-3xl font-bold ${healthColors.text}`}>{result.healthScore.score}</div>
                        <div className={`text-lg font-bold ${healthColors.text}`}>{result.healthScore.grade}</div>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">{t('lab.healthScore')}</div>
                    {result.cmpMode === 'auto-consent' && result.cmpClicked && (
                        <div className="text-[10px] text-green-400 mt-1">{t('lab.cmpAutoConsent')}</div>
                    )}
                </div>
            )}
            
            {result.screenshot && (
                <div className="w-40 flex-shrink-0">
                    <img 
                        src={result.screenshot} 
                        alt="Screenshot" 
                        className="w-full rounded border border-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                        {result.device === 'mobile' ? t('lab.devices.mobile') : t('lab.devices.desktop')}
                    </p>
                </div>
            )}
            
            <div className="flex-1">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${getScoreColor(result.metrics?.lcpScore)}`}>
                        <div className="text-xs opacity-70">LCP</div>
                        <div className="text-xl font-bold">{formatMs(result.metrics?.lcp)}</div>
                    </div>
                    <div className={`p-3 rounded-lg ${getScoreColor(result.metrics?.clsScore)}`}>
                        <div className="text-xs opacity-70">CLS</div>
                        <div className="text-xl font-bold">{result.metrics?.cls?.toFixed(3) || '-'}</div>
                    </div>
                    <div className={`p-3 rounded-lg ${getScoreColor(result.metrics?.fcpScore)}`}>
                        <div className="text-xs opacity-70">FCP</div>
                        <div className="text-xl font-bold">{formatMs(result.metrics?.fcp)}</div>
                    </div>
                    <div className={`p-3 rounded-lg ${getScoreColor(result.metrics?.ttfbScore)}`}>
                        <div className="text-xs opacity-70">TTFB</div>
                        <div className="text-xl font-bold">{formatMs(result.metrics?.ttfb)}</div>
                    </div>
                </div>
                
                {result.lighthouse && !result.lighthouse.error && (
                    <div className="grid grid-cols-4 gap-2">
                        {Object.entries(result.lighthouse.scores).map(([key, score]) => (
                            <div key={key} className="text-center">
                                <div className={`w-12 h-12 mx-auto rounded-full border-2 flex items-center justify-center text-lg font-bold ${getLighthouseScoreColor(score)}`}>
                                    {score}
                                </div>
                                <div className="text-[10px] text-gray-400 mt-1 capitalize">
                                    {key === 'bestPractices' ? t('lab.bestPractices') : key}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.navigationTiming')}</h4>
                <div className="space-y-2 text-sm">
                    {['dns', 'tcp', 'ssl', 'download', 'domInteractive', 'domContentLoaded', 'loadComplete'].map(key => (
                        <div key={key} className="flex justify-between">
                            <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                            <span className="text-white">{formatMs(result.navigationTiming?.[key])}</span>
                        </div>
                    ))}
                </div>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.resourceBreakdown')}</h4>
                <div className="space-y-2 text-sm">
                    {Object.entries(result.resources?.breakdown || {}).map(([type, data]) => (
                        <div key={type} className="flex justify-between items-center">
                            <span className="text-gray-500 capitalize">{type}</span>
                            <div className="flex gap-4">
                                <span className="text-gray-400">{data.count}×</span>
                                <span className="text-white w-16 text-right">{formatBytes(data.size)}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-700 flex justify-between">
                    <span className="text-gray-400">{t('lab.total')}</span>
                    <span className="text-white font-medium">{formatBytes(result.resources?.totalSize || 0)}</span>
                </div>
            </div>
        </div>
    </div>
    );
};

const LighthouseTab = ({ lighthouse, formatMs, getLighthouseScoreColor, t }) => (
    <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
            {Object.entries(lighthouse.scores).map(([key, score]) => (
                <div key={key} className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className={`w-16 h-16 mx-auto rounded-full border-3 flex items-center justify-center text-2xl font-bold ${getLighthouseScoreColor(score)}`}>
                        {score}
                    </div>
                    <div className="text-sm text-gray-400 mt-2 capitalize">
                        {key === 'bestPractices' ? t('lab.bestPractices') : key}
                    </div>
                </div>
            ))}
        </div>
        
        <div className="bg-gray-800/50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.performanceMetrics')}</h4>
            <div className="grid grid-cols-3 gap-4">
                {Object.entries(lighthouse.metrics || {}).filter(([_, v]) => v).map(([key, value]) => (
                    <div key={key} className="text-center p-3 bg-gray-900/50 rounded">
                        <div className="text-lg font-bold text-white">{formatMs(value)}</div>
                        <div className="text-xs text-gray-400 uppercase">{key}</div>
                    </div>
                ))}
            </div>
        </div>
        
        {lighthouse.opportunities?.length > 0 && (
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.opportunities')}</h4>
                <div className="space-y-2">
                    {lighthouse.opportunities.map((opp, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-900/50 rounded">
                            <div className="flex-1">
                                <div className="text-sm text-white">{opp.title}</div>
                                <div className="text-xs text-gray-500 line-clamp-1">{opp.description}</div>
                            </div>
                            {opp.savings > 0 && (
                                <span className="text-green-400 text-sm ml-2">
                                    -{formatMs(opp.savings)}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        )}
        
        {lighthouse.diagnostics?.length > 0 && (
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.diagnostics')}</h4>
                <div className="space-y-2">
                    {lighthouse.diagnostics.map((diag, i) => (
                        <div key={i} className="p-2 bg-gray-900/50 rounded">
                            <div className="text-sm text-white">{diag.title}</div>
                            <div className="text-xs text-gray-500 line-clamp-2">{diag.description}</div>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);

const AccessibilityTab = ({ accessibility, getImpactColor, t }) => (
    <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
            <div className="bg-red-500/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-400">{accessibility.summary?.critical || 0}</div>
                <div className="text-xs text-gray-400">{t('lab.accessibility.critical')}</div>
            </div>
            <div className="bg-orange-500/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-400">{accessibility.summary?.serious || 0}</div>
                <div className="text-xs text-gray-400">{t('lab.accessibility.serious')}</div>
            </div>
            <div className="bg-yellow-500/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-yellow-400">{accessibility.summary?.moderate || 0}</div>
                <div className="text-xs text-gray-400">{t('lab.accessibility.moderate')}</div>
            </div>
            <div className="bg-green-500/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-400">{accessibility.passes || 0}</div>
                <div className="text-xs text-gray-400">{t('lab.accessibility.passed')}</div>
            </div>
        </div>
        
        {accessibility.violations?.length > 0 ? (
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">
                    {t('lab.accessibility.issuesFound')} ({accessibility.violations.length})
                </h4>
                <div className="space-y-3">
                    {accessibility.violations.map((v, i) => (
                        <div key={i} className="p-3 bg-gray-900/50 rounded border-l-2 border-l-red-500">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded text-xs ${getImpactColor(v.impact)}`}>
                                    {v.impact}
                                </span>
                                <span className="text-white text-sm font-medium">{v.help}</span>
                            </div>
                            <p className="text-xs text-gray-400 mb-2">{v.description}</p>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-gray-500">{t('lab.accessibility.affected')}: {v.nodes} {t('lab.accessibility.elements')}</span>
                                {v.wcagTags?.length > 0 && (
                                    <span className="text-blue-400">{v.wcagTags.join(', ')}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ) : (
            <div className="bg-green-500/20 rounded-lg p-6 text-center">
                <div className="text-2xl mb-2">✓</div>
                <div className="text-green-400">{t('lab.accessibility.noIssues')}</div>
            </div>
        )}
    </div>
);

const ThirdPartyTab = ({ thirdParty, formatBytes, getCategoryIcon, getCategoryColor, t }) => (
    <div className="space-y-6">
        {thirdParty.cmpConflict?.entities?.length >= 2 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <div className="text-sm font-medium text-red-300">⚠️ {t('lab.cmpConflictTitle')}: {thirdParty.cmpConflict.entities.join(', ')}</div>
                <p className="text-xs text-red-200/70 mt-1">{t('lab.cmpConflictText')}</p>
            </div>
        )}
        <div className="grid grid-cols-4 gap-3">
            <div className="bg-green-500/20 rounded-lg p-3">
                <div className="text-xs text-gray-400">{t('lab.thirdParty.firstParty')}</div>
                <div className="text-xl font-bold text-green-400">{thirdParty.summary?.firstPartyCount || 0}</div>
                <div className="text-xs text-gray-500">{formatBytes(thirdParty.summary?.firstPartySize || 0)}</div>
            </div>
            <div className="bg-orange-500/20 rounded-lg p-3">
                <div className="text-xs text-gray-400">{t('lab.thirdParty.thirdParty')}</div>
                <div className="text-xl font-bold text-orange-400">{thirdParty.summary?.thirdPartyCount || 0}</div>
                <div className="text-xs text-gray-500">{formatBytes(thirdParty.summary?.thirdPartySize || 0)}</div>
            </div>
            <div className="bg-blue-500/20 rounded-lg p-3">
                <div className="text-xs text-gray-400">{t('lab.thirdParty.recognized')}</div>
                <div className="text-xl font-bold text-blue-400">{thirdParty.summary?.categorizedCount || 0}</div>
                <div className="text-xs text-gray-500">{t('lab.thirdParty.scripts')}</div>
            </div>
            <div className="bg-gray-500/20 rounded-lg p-3">
                <div className="text-xs text-gray-400">{t('lab.thirdParty.unknown')}</div>
                <div className="text-xl font-bold text-gray-400">{thirdParty.summary?.unknownCount || 0}</div>
                <div className="text-xs text-gray-500">{t('lab.thirdParty.scripts')}</div>
            </div>
        </div>
        
        {thirdParty.categories?.length > 0 && (
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.thirdParty.categories')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {thirdParty.categories.map((cat, i) => (
                        <div key={i} className={`p-3 rounded border ${getCategoryColor(cat.category)}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <span>{getCategoryIcon(cat.category)}</span>
                                <span className="text-sm font-medium">{cat.label}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                                <span>{cat.count} {t('lab.thirdParty.requests')}</span>
                                <span>{formatBytes(cat.size)}</span>
                            </div>
                            {cat.entities?.length > 0 && (
                                <div className="text-[10px] text-gray-400 mt-1 line-clamp-1">
                                    {cat.entities.slice(0, 3).join(', ')}
                                    {cat.entities.length > 3 && ` +${cat.entities.length - 3}`}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        )}
        
        {thirdParty.domains?.length > 0 && (
            <div className="bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">
                    {t('lab.thirdParty.domains')} ({thirdParty.domains.length})
                </h4>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                    {thirdParty.domains.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-gray-900/50 rounded">
                            <div className="flex items-center gap-2">
                                <span className="text-sm">{getCategoryIcon(d.category)}</span>
                                <div>
                                    <div className="text-sm text-white">{d.entity || d.domain}</div>
                                    <div className="text-[10px] text-gray-500">{d.domain}</div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-white">{formatBytes(d.size)}</div>
                                <div className="text-xs text-gray-500">{d.count}×</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </div>
);

const CoverageTab = ({ coverage, formatBytes, t }) => {
    const getUnusedColor = (percent) => {
        if (percent >= 70) return 'text-red-400';
        if (percent >= 40) return 'text-yellow-400';
        return 'text-green-400';
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-300">{t('lab.coverage.jsCoverage')}</h4>
                        <span className={`text-lg font-bold ${getUnusedColor(coverage.js?.unusedPercent)}`}>
                            {coverage.js?.unusedPercent || 0}% {t('lab.coverage.unused')}
                        </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3 mb-3">
                        <div 
                            className="bg-green-500 h-3 rounded-full" 
                            style={{ width: `${100 - (coverage.js?.unusedPercent || 0)}%` }}
                        ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                        <span>{t('lab.coverage.used')}: {formatBytes(coverage.js?.usedBytes || 0)}</span>
                        <span>{t('lab.coverage.unused')}: {formatBytes(coverage.js?.unusedBytes || 0)}</span>
                    </div>
                </div>
                
                <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-300">{t('lab.coverage.cssCoverage')}</h4>
                        <span className={`text-lg font-bold ${getUnusedColor(coverage.css?.unusedPercent)}`}>
                            {coverage.css?.unusedPercent || 0}% {t('lab.coverage.unused')}
                        </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3 mb-3">
                        <div 
                            className="bg-blue-500 h-3 rounded-full" 
                            style={{ width: `${100 - (coverage.css?.unusedPercent || 0)}%` }}
                        ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                        <span>{t('lab.coverage.used')}: {formatBytes(coverage.css?.usedBytes || 0)}</span>
                        <span>{t('lab.coverage.unused')}: {formatBytes(coverage.css?.unusedBytes || 0)}</span>
                    </div>
                </div>
            </div>
            
            {coverage.js?.files?.length > 0 && (
                <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.coverage.largestJs')}</h4>
                    <div className="space-y-2">
                        {coverage.js.files.map((f, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 bg-gray-900/50 rounded">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-white truncate">{f.url}</div>
                                </div>
                                <div className="text-xs text-gray-400">{formatBytes(f.totalBytes)}</div>
                                <div className={`text-xs font-medium whitespace-nowrap ${getUnusedColor(f.unusedPercent)}`}>
                                    {f.unusedPercent}% {t('lab.coverage.unused')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {coverage.css?.files?.length > 0 && (
                <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">{t('lab.coverage.largestCss')}</h4>
                    <div className="space-y-2">
                        {coverage.css.files.map((f, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 bg-gray-900/50 rounded">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-white truncate">{f.url}</div>
                                </div>
                                <div className="text-xs text-gray-400">{formatBytes(f.totalBytes)}</div>
                                <div className={`text-xs font-medium whitespace-nowrap ${getUnusedColor(f.unusedPercent)}`}>
                                    {f.unusedPercent}% {t('lab.coverage.unused')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const SecurityTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-white">Security Headers</h4>
            <span className={`text-2xl font-bold ${data.score >= 80 ? 'text-green-400' : data.score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                {data.score}/100
            </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.checks && Object.entries(data.checks).map(([key, check]) => (
                <div key={key} className={`p-3 rounded-lg border ${check.present ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                    <div className="flex items-center gap-2">
                        <span className={check.present ? 'text-green-400' : 'text-red-400'}>{check.present ? '✓' : '✗'}</span>
                        <span className="text-white font-medium uppercase text-sm">{key}</span>
                    </div>
                    {check.value && <div className="text-xs text-gray-400 mt-1 truncate">{check.value}</div>}
                </div>
            ))}
        </div>
    </div>
);

const SchemaTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">📋</span>
            <div>
                <h4 className="text-lg font-medium text-white">Schema.org / SEO</h4>
                <p className="text-sm text-gray-400">{data.schemaTypes?.length || 0} schema types found</p>
            </div>
        </div>
        {data.schemaTypes?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {data.schemaTypes.map((type, i) => (
                    <div key={i} className="px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-sm">
                        {type}
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 text-gray-500">No schema markup found</div>
        )}
        {data.metaTags && (
            <div className="mt-4">
                <h5 className="text-sm font-medium text-gray-300 mb-2">Meta Tags</h5>
                <div className="space-y-2">
                    {data.metaTags.title && <div className="text-xs"><span className="text-gray-500">Title:</span> <span className="text-white">{data.metaTags.title}</span></div>}
                    {data.metaTags.description && <div className="text-xs"><span className="text-gray-500">Description:</span> <span className="text-white">{data.metaTags.description}</span></div>}
                </div>
            </div>
        )}
    </div>
);

const TechStackTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">⚙️</span>
            <div>
                <h4 className="text-lg font-medium text-white">Tech Stack</h4>
                <p className="text-sm text-gray-400">{data.technologies?.length || 0} technologies detected</p>
            </div>
        </div>
        {data.technologies?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {data.technologies.map((tech, i) => (
                    <div key={i} className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="flex items-center gap-2">
                            {tech.icon && <img src={tech.icon} alt="" className="w-5 h-5" />}
                            <span className="text-white font-medium">{tech.name}</span>
                        </div>
                        {tech.version && <div className="text-xs text-gray-400 mt-1">v{tech.version}</div>}
                        {tech.category && <div className="text-xs text-purple-400 mt-1">{tech.category}</div>}
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 text-gray-500">No technologies detected</div>
        )}
    </div>
);

const DnsSslTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.ssl && (
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                    <h5 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                        <span className={data.ssl.valid ? 'text-green-400' : 'text-red-400'}>{data.ssl.valid ? '🔒' : '🔓'}</span>
                        SSL Certificate
                    </h5>
                    <div className="space-y-2 text-xs">
                        <div><span className="text-gray-500">Issuer:</span> <span className="text-white">{data.ssl.issuer}</span></div>
                        <div><span className="text-gray-500">Valid until:</span> <span className="text-white">{data.ssl.validTo}</span></div>
                        <div><span className="text-gray-500">Days remaining:</span> <span className={data.ssl.daysRemaining > 30 ? 'text-green-400' : 'text-yellow-400'}>{data.ssl.daysRemaining}</span></div>
                    </div>
                </div>
            )}
            {data.dns && (
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                    <h5 className="text-sm font-medium text-white mb-3">DNS Records</h5>
                    <div className="space-y-2 text-xs">
                        {data.dns.a && <div><span className="text-gray-500">A:</span> <span className="text-white">{data.dns.a.join(', ')}</span></div>}
                        {data.dns.aaaa && <div><span className="text-gray-500">AAAA:</span> <span className="text-white">{data.dns.aaaa.join(', ')}</span></div>}
                        {data.dns.mx && <div><span className="text-gray-500">MX:</span> <span className="text-white">{data.dns.mx.length} records</span></div>}
                    </div>
                </div>
            )}
        </div>
    </div>
);

const CarbonTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-full border-4 flex items-center justify-center ${data.isGreen ? 'border-green-500 bg-green-500/10' : 'border-gray-500 bg-gray-500/10'}`}>
                <span className="text-3xl">{data.isGreen ? '🌱' : '🏭'}</span>
            </div>
            <div>
                <h4 className="text-lg font-medium text-white">{data.isGreen ? 'Green Hosting' : 'Standard Hosting'}</h4>
                <p className="text-sm text-gray-400">{data.co2PerView ? `${data.co2PerView}g CO₂/view` : 'CO₂ data unavailable'}</p>
            </div>
        </div>
        {data.cleanerThan && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="text-green-400 font-medium">Cleaner than {data.cleanerThan}% of websites</div>
            </div>
        )}
    </div>
);

const GtmTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">📊</span>
            <div>
                <h4 className="text-lg font-medium text-white">Google Tag Manager Audit</h4>
                <p className="text-sm text-gray-400">{data.containerId || 'No GTM found'}</p>
            </div>
        </div>
        {data.tags?.length > 0 ? (
            <div className="space-y-2">
                {data.tags.map((tag, i) => (
                    <div key={i} className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                        <div className="text-white font-medium">{tag.name}</div>
                        <div className="text-xs text-gray-400">{tag.type}</div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 text-gray-500">{data.error || 'No GTM tags found'}</div>
        )}
    </div>
);

const MediaTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-400">{data.images?.count || 0}</div>
                <div className="text-xs text-gray-400">Images</div>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-400">{data.videos?.count || 0}</div>
                <div className="text-xs text-gray-400">Videos</div>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-cyan-400">{data.svgs?.count || 0}</div>
                <div className="text-xs text-gray-400">SVGs</div>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-orange-400">{data.iframes?.count || 0}</div>
                <div className="text-xs text-gray-400">Iframes</div>
            </div>
        </div>
        {data.issues?.length > 0 && (
            <div className="mt-4">
                <h5 className="text-sm font-medium text-red-400 mb-2">Issues ({data.issues.length})</h5>
                <div className="space-y-2">
                    {data.issues.slice(0, 5).map((issue, i) => (
                        <div key={i} className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300">{issue}</div>
                    ))}
                </div>
            </div>
        )}
    </div>
);

const AccessibilityAuditTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">♿</span>
            <div>
                <h4 className="text-lg font-medium text-white">WCAG Accessibility Audit</h4>
                <p className="text-sm text-gray-400">{data.score ? `Score: ${data.score}/100` : 'Audit complete'}</p>
            </div>
        </div>
        {data.issues?.length > 0 ? (
            <div className="space-y-2">
                {data.issues.map((issue, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${issue.impact === 'critical' ? 'bg-red-500/10 border-red-500/30' : issue.impact === 'serious' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                        <div className="text-white font-medium">{issue.message}</div>
                        <div className="text-xs text-gray-400 mt-1">{issue.wcag}</div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 text-green-400">No accessibility issues found</div>
        )}
    </div>
);

const StatusPageTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">📡</span>
            <div>
                <h4 className="text-lg font-medium text-white">Status Page Detection</h4>
                <p className="text-sm text-gray-400">{data.detected ? 'Status page found' : 'No status page detected'}</p>
            </div>
        </div>
        {data.detected && data.url && (
            <a href={data.url} target="_blank" rel="noopener noreferrer" className="block p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors">
                <div className="text-blue-400">{data.url}</div>
                <div className="text-xs text-gray-400 mt-1">{data.provider || 'Status Page'}</div>
            </a>
        )}
    </div>
);

const UptimeTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className={`text-2xl font-bold ${data.status === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                    {data.status === 'up' ? '✓' : '✗'}
                </div>
                <div className="text-xs text-gray-400">Status</div>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-400">{data.responseTime || '-'}ms</div>
                <div className="text-xs text-gray-400">Response Time</div>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-400">{data.statusCode || '-'}</div>
                <div className="text-xs text-gray-400">Status Code</div>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg text-center">
                <div className="text-2xl font-bold text-cyan-400">{data.uptime || '-'}%</div>
                <div className="text-xs text-gray-400">Uptime</div>
            </div>
        </div>
    </div>
);

const RedirectsTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">↪️</span>
            <div>
                <h4 className="text-lg font-medium text-white">Redirect Chain Analysis</h4>
                <p className="text-sm text-gray-400">{data.chain?.length || 0} redirects detected</p>
            </div>
        </div>
        {data.chain?.length > 0 ? (
            <div className="space-y-2">
                {data.chain.map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step.status >= 300 && step.status < 400 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>
                            {step.status}
                        </div>
                        <div className="flex-1 text-xs text-white truncate">{step.url}</div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 text-green-400">No redirects - direct access</div>
        )}
    </div>
);

const BrokenLinksTab = ({ data, t }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🔗</span>
            <div>
                <h4 className="text-lg font-medium text-white">Broken Links Detection</h4>
                <p className="text-sm text-gray-400">{data.broken?.length || 0} broken links found</p>
            </div>
        </div>
        {data.broken?.length > 0 ? (
            <div className="space-y-2">
                {data.broken.map((link, i) => (
                    <div key={i} className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-center gap-2">
                            <span className="text-red-400 font-bold">{link.status}</span>
                            <span className="text-white text-sm truncate">{link.url}</span>
                        </div>
                        {link.source && <div className="text-xs text-gray-400 mt-1">Found on: {link.source}</div>}
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-8 text-green-400">No broken links found</div>
        )}
    </div>
);

export default LabTestPanel;
